"use client";

import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useSyncExternalStore,
  startTransition,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import dynamic from "next/dynamic";
import { useRouter } from "next/navigation";
import type { KundaliResponse } from "@/lib/api/kundali";
import {
  makeHandleSubmit,
  makeRetryKundali,
  type KundaliRetryState,
} from "./KundaliFormCard.submit";
import { useFieldFunnel } from "./useFieldFunnel";
import type { PlaceResult } from "@/lib/api/misc";
// Kundali-form analytics imports go straight to the source module instead of
// re-routing through `@/lib/analytics`. The barrel re-export there
// (`export * from "@/lib/analytics-kundali"`) created a chunk-resolution race
// in production builds — `(0, o.trackKundaliFormStarted) is not a function`
// (Sentry 7481455768) fired when the consumer chunk loaded before the kundali
// chunk hydrated the namespace object. Direct imports avoid the indirection.
import { trackKundaliFormStarted } from "@/lib/analytics-kundali";
import {
  DailyInsightSlot,
  KundaliRetryCard,
  useDeferredDailyInsight,
} from "./KundaliFormCard.controls";
import { KundaliInputForm, type CapturedSEOEntry } from "./KundaliFormCard.parts";
import type { KundaliProgressiveCopy } from "./KundaliFormCard.progressive";
import { getKundaliTranslations, type KundaliLang } from "./KundaliFormCard.i18n";
import { LOADING_STEPS, loadingTimers } from "./KundaliFormCard.loading";
import { useCapturedSEOEntry } from "./KundaliFormCard.seo";
import type { KundaliFieldErrors } from "./KundaliFormCard.validation";
import DailyPanchangLink from "@/components/DailyPanchangLink";
import KundaliSavedLink from "./KundaliSavedLink";
import { isContactGateSatisfied } from "./KundaliFormCard.validation";
import { useReadingPrefill } from "./KundaliFormCard.prefill";
import type { KundaliField } from "@/lib/analytics-kundali";
import { useInitialKundaliFieldFocus } from "./useInitialKundaliFieldFocus";
import { KundaliResultBoundary } from "./KundaliFormCard.result";

const SpiritualReveal = dynamic(() => import("@ishvaram/ui/SpiritualReveal"), { ssr: false });
const POST_RESULT_ADJUNCTS_MIN_HEIGHT = 220;
const DAILY_PANCHANG_LINK_MIN_HEIGHT = 92;

function subscribeToFormReady(onReady: () => void) {
  const id = window.setTimeout(onReady, 0);
  return () => window.clearTimeout(id);
}

const formReadyClientSnapshot = () => true;
const formReadyServerSnapshot = () => false;

function useFormReady(initialFocusField?: KundaliField) {
  const ready = useSyncExternalStore(
    subscribeToFormReady,
    formReadyClientSnapshot,
    formReadyServerSnapshot
  );
  useInitialKundaliFieldFocus(initialFocusField, ready);
  return ready;
}

// Warm the result chunk the moment the seeker starts the form, so by the time
// they submit, the `dynamic(ssr:false)` chunk is already in the module cache and
// the result swaps in within a frame — no `Suspense` gap (CLS). `dynamic()` and
// this raw `import()` resolve the SAME webpack module, so one warms the other.
function usePreloadKundaliResultOnFormStart(formStarted: boolean) {
  useEffect(() => {
    if (formStarted) void import("@/components/KundaliResult");
  }, [formStarted]);
}

export type KundaliFormCardProps = {
  defaultLanguage?: KundaliLang;
  source?: string;
  submitLabel?: string;
  savedNote?: string;
  savedNoteHi?: string;
  phoneReason?: string;
  phoneReasonHi?: string;
  showDailyInsightCard?: boolean;
  containerClassName?: string;
  formClassName?: string;
  namePlaceholder?: string;
  placePlaceholder?: string;
  requireBirthTime?: boolean;
  reassuranceNote?: string;
  reassuranceNoteHi?: string;
  loadingLabel?: string;
  loadingLabelHi?: string;
  seoEntryOverride?: CapturedSEOEntry | null;
  progressiveCopy?: KundaliProgressiveCopy;
  initialFocusField?: KundaliField;
  showIntentBanner?: boolean;
  /**
   * Optional override for what renders BELOW the inline kundali result on the
   * result-first layout. REPLACES the default saved-link + daily-panchang
   * adjuncts so a host surface can render its own intent-matched value-moment
   * block (e.g. the free career snapshot + the career_report DepthPaywall on
   * /career-astrology). The inline result itself is unchanged — additive,
   * monetization stays on the value surface (rule 46), commits with the result
   * chunk so no card shifts in first (rule 58).
   */
  renderPostResult?: (result: KundaliResponse, language: KundaliLang) => ReactNode;
};

export default function KundaliFormCard({
  defaultLanguage = "en",
  source = "home",
  submitLabel,
  savedNote,
  savedNoteHi,
  phoneReason,
  phoneReasonHi,
  showDailyInsightCard = true,
  containerClassName = "mx-auto max-w-md px-4 sm:px-6 pb-16",
  formClassName,
  namePlaceholder,
  placePlaceholder,
  requireBirthTime = false,
  reassuranceNote,
  reassuranceNoteHi,
  loadingLabel,
  loadingLabelHi,
  seoEntryOverride,
  progressiveCopy,
  initialFocusField,
  showIntentBanner = true,
  renderPostResult,
}: KundaliFormCardProps) {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<KundaliFieldErrors>({});
  const [result, setResult] = useState<KundaliResponse | null>(null);
  const [language, setLanguage] = useState<KundaliLang>(defaultLanguage);
  const [activeField, setActiveField] = useState<string | null>(null);
  const [formStarted, setFormStarted] = useState(false);
  const [formReady, loadingStep] = [useFormReady(initialFocusField), useLoadingStep(loading)];
  const showDailyInsight = useDeferredDailyInsight();
  const seoEntry = useResolvedSEOEntry(seoEntryOverride);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [birthTime, setBirthTime] = useState("");
  const [birthPlace, setBirthPlace] = useState("");
  const [selectedPlace, setSelectedPlace] = useState<PlaceResult | null>(null);
  const [ambiguousSuggestion, setAmbiguousSuggestion] = useState<PlaceResult | null>(null);
  const [retryState, setRetryState] = useState<KundaliRetryState>(null);
  useReadingPrefill({ setName, setBirthDate, setBirthTime, setBirthPlace, setSelectedPlace });
  // Stable across keystrokes (setState setters are stable) so the per-field
  // handlers below can be memoized and the memo'd field components can skip
  // re-render when a sibling field changes (INP fix, 2026-07-22).
  const updateField = useMemo(() => makeFieldUpdater(setError, setFieldErrors), [
    setError,
    setFieldErrors,
  ]);
  const fieldFunnel = useFieldFunnel({
    source: seoEntry ? `seo:${seoEntry.config.intent}` : source,
    source_path: seoEntry?.sourcePath,
    language,
  });
  const markFormStarted = useMarkFormStarted({
    setActiveField,
    formStarted,
    setFormStarted,
    language,
    seoEntry,
    source,
  });
  const submitDeps = {
    seoEntry,
    source,
    requireBirthTime,
    name,
    phone,
    email,
    birthDate,
    birthTime,
    birthPlace,
    language,
    selectedPlace,
    router,
    setLoading,
    setError,
    setFieldErrors,
    setResult,
    setSelectedPlace,
    setAmbiguousSuggestion,
    setRetryState,
  };
  const handleSubmit = makeHandleSubmit(submitDeps);
  const retryKundali = retryState ? makeRetryKundali(submitDeps, retryState.idempotencyKey) : null;
  usePreloadKundaliResultOnFormStart(formStarted);
  // Memoized (and computed before the early return so the hook is
  // unconditional) so the stable object lets the memo'd field components skip
  // re-render on a sibling field's keystroke (INP fix, 2026-07-22).
  const t = useMemo(
    () =>
      getKundaliTranslations(language, {
        submitLabel,
        savedNote: localizedOverride(language, savedNote, savedNoteHi),
        loadingLabel: localizedOverride(language, loadingLabel, loadingLabelHi),
        namePlaceholder,
        placePlaceholder,
        phoneReason: localizedOverride(language, phoneReason, phoneReasonHi),
      }),
    [
      language,
      submitLabel,
      savedNote,
      savedNoteHi,
      loadingLabel,
      loadingLabelHi,
      namePlaceholder,
      placePlaceholder,
      phoneReason,
      phoneReasonHi,
    ]
  );
  // Stable field handlers (updateField + the setState setters are stable) so the
  // memo'd field components skip re-render when a sibling field changes (INP).
  const setNameField = useCallback((v: string) => updateField("name", v, setName), [updateField]);
  const setPhoneField = useCallback(
    (v: string) => updateField("phone", v, setPhone),
    [updateField]
  );
  const setEmailField = useCallback(
    (v: string) => updateField("email", v, setEmail),
    [updateField]
  );
  const setBirthDateField = useCallback(
    (v: string) => updateField("birthDate", v, setBirthDate),
    [updateField]
  );
  const setBirthPlaceField = useCallback(
    (v: string) => updateField("birthPlace", v, setBirthPlace),
    [updateField]
  );
  const clearPlace = useCallback(() => {
    setSelectedPlace(null);
    setAmbiguousSuggestion(null);
    updateField("birthPlace", "", setBirthPlace);
  }, [updateField]);

  if (result)
    return renderKundaliResult(result, language, source, () => setResult(null), renderPostResult);

  return (
    <>
      <KundaliSpiritualReveal loading={loading} language={language} loadingStep={loadingStep} />
      <KundaliInputForm
        containerClassName={containerClassName}
        formClassName={formClassName}
        onSubmit={handleSubmit}
        seoEntry={seoEntry}
        language={language}
        setLanguage={setLanguage}
        formReady={formReady}
        name={name}
        setName={setNameField}
        phone={phone}
        setPhone={setPhoneField}
        email={email}
        setEmail={setEmailField}
        birthDate={birthDate}
        setBirthDate={setBirthDateField}
        birthTime={birthTime}
        setBirthTime={setBirthTime}
        requireBirthTime={requireBirthTime}
        birthPlace={birthPlace}
        setBirthPlace={setBirthPlaceField}
        setSelectedPlace={setSelectedPlace}
        selectedPlace={selectedPlace}
        onClearPlace={clearPlace}
        ambiguousSuggestion={ambiguousSuggestion}
        fieldFunnel={fieldFunnel}
        activeField={activeField}
        setActiveField={setActiveField}
        markFormStarted={markFormStarted}
        fieldErrors={fieldErrors}
        error={error}
        loading={loading}
        loadingStep={loadingStep}
        reassuranceNote={localizedOverride(language, reassuranceNote, reassuranceNoteHi)}
        deliveryField={null} // ADR-0026 second contact field removed 2026-06-23 (rule 39)
        submitDisabled={!formReady || !isContactGateSatisfied(phone)}
        retryCard={renderRetryCard(retryState, retryKundali, loading, t)}
        submitLabelOverride={submitLabel}
        t={t}
        progressiveCopy={progressiveCopy}
        source={seoEntry ? `seo:${seoEntry.config.intent}` : source}
        sourcePath={seoEntry?.sourcePath}
        initialFocusField={initialFocusField}
        showIntentBanner={showIntentBanner}
      />
      <DailyInsightSlot show={showDailyInsightCard} ready={showDailyInsight} language={language} />
    </>
  );
}

function useResolvedSEOEntry(override: CapturedSEOEntry | null | undefined) {
  const captured = useCapturedSEOEntry(override);
  return override !== undefined ? override : captured;
}

function renderRetryCard(
  retryState: KundaliRetryState,
  retryKundali: (() => void) | null,
  loading: boolean,
  t: ReturnType<typeof getKundaliTranslations>
) {
  return (
    <RetryCardSlot retryState={retryState} retryKundali={retryKundali} loading={loading} t={t} />
  );
}

// Extracted so the main component stays under max-lines-per-function; renders the
// retry card only when a retryable failure is in flight.
function RetryCardSlot({
  retryState,
  retryKundali,
  loading,
  t,
}: {
  retryState: KundaliRetryState;
  retryKundali: (() => void) | null;
  loading: boolean;
  t: ReturnType<typeof getKundaliTranslations>;
}) {
  if (!retryState || !retryKundali) return null;
  return (
    <KundaliRetryCard
      title={t.retryTitle}
      body={t.retryBody}
      actionLabel={t.retryAction}
      onRetry={retryKundali}
      loading={loading}
    />
  );
}

function localizedOverride(language: KundaliLang, en?: string, hi?: string): string | undefined {
  return language === "hi" ? (hi ?? en) : en;
}

function useMarkFormStarted(args: {
  setActiveField: (field: string) => void;
  formStarted: boolean;
  setFormStarted: (value: boolean) => void;
  language: KundaliLang;
  seoEntry: CapturedSEOEntry | null;
  source: string;
}) {
  const { setActiveField, formStarted, setFormStarted, language, seoEntry, source } = args;
  return useCallback(
    (firstField: string) => {
      setActiveField(firstField);
      if (formStarted) return;
      setFormStarted(true);
      trackKundaliFormStarted({
        language,
        first_field: firstField,
        source: seoEntry ? `seo:${seoEntry.config.intent}` : source,
        source_path:
          seoEntry?.sourcePath ||
          (typeof window !== "undefined" ? window.location.pathname || undefined : undefined),
      });
    },
    [setActiveField, formStarted, setFormStarted, language, seoEntry, source]
  );
}

function makeFieldUpdater(
  setError: Dispatch<SetStateAction<string | null>>,
  setFieldErrors: Dispatch<SetStateAction<KundaliFieldErrors>>
) {
  return function updateField<K extends keyof KundaliFieldErrors>(
    field: K,
    value: string,
    setter: (next: string) => void
  ) {
    setter(value);
    setError(null);
    setFieldErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  };
}

function renderKundaliResult(
  result: KundaliResponse,
  language: KundaliLang,
  source: string,
  onBack: () => void,
  renderPostResult?: (result: KundaliResponse, language: KundaliLang) => ReactNode
) {
  const layout = source === "ai_astrologer" ? "chat-first" : "result-first";
  const postResultAdjuncts =
    layout === "result-first"
      ? (renderPostResult?.(result, language) ??
        (result.id ? <KundaliPostResultAdjuncts result={result} language={language} /> : null))
      : null;
  const content = (
    <KundaliResultBoundary
      result={result}
      language={language}
      onBack={onBack}
      layout={layout}
      postResultAdjuncts={postResultAdjuncts}
    />
  );
  if (layout !== "chat-first") {
    return content;
  }
  return <div className="ai-chat-result-active">{content}</div>;
}

function KundaliPostResultAdjuncts({
  result,
  language,
}: {
  result: KundaliResponse;
  language: KundaliLang;
}) {
  // Result-first entries keep the inline result AND get its durable return
  // path: every generated kundali is persisted under result.id, and
  // /kundali/<id> re-renders the same KundaliResult tree (rule 46 — the
  // inline value moment stays put; the saved link is additive). Keep these
  // adjuncts inside KundaliResult so the dynamic result chunk and cards commit
  // together instead of the cards rendering first and shifting downward.
  return (
    <div style={{ minHeight: POST_RESULT_ADJUNCTS_MIN_HEIGHT }}>
      <KundaliSavedLink readingId={result.id} language={language} />
      <div style={{ minHeight: DAILY_PANCHANG_LINK_MIN_HEIGHT }}>
        <DailyPanchangLink
          readingId={result.id}
          language={language === "hi" ? "hi" : "en"}
          source="kundali_result"
        />
      </div>
    </div>
  );
}

function useLoadingStep(loading: boolean) {
  const [loadingStep, setLoadingStep] = useState(0);
  useEffect(() => {
    startTransition(() => setLoadingStep(0));
    if (!loading) return;
    const timers = loadingTimers(setLoadingStep);
    return () => timers.forEach(clearTimeout);
  }, [loading]);
  return loadingStep;
}

function KundaliSpiritualReveal({
  loading,
  language,
  loadingStep,
}: {
  loading: boolean;
  language: KundaliLang;
  loadingStep: number;
}) {
  if (!loading) return null;
  return (
    <SpiritualReveal loading language={language} steps={LOADING_STEPS} currentStep={loadingStep} />
  );
}
